import styled from 'styled-components';
import { User, Phone, Mail, Calendar, Hash, MapPin, FileText, Pencil } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { fullName } from '../../utils/nameUtils';
import type { Client } from '../../types/clients';

interface OverviewTabProps {
  client: Client;
  onEditClick: () => void;
  /** When false (default), the Edit button is hidden — mirrors the drawer's action guard. */
  canManage?: boolean;
}

// ==================== Styled Components ====================

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing[6]};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  padding: ${({ theme }) => theme.spacing[6]};
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const CardTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const InfoList = styled.dl`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const InfoRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  align-items: flex-start;
`;

const InfoIcon = styled.div`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  color: ${({ theme }) => theme.colors.text.light};
  margin-top: 1px;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const InfoContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const InfoLabel = styled.dt`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.light};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const InfoValue = styled.dd`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const EmptyValue = styled.dd`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.light};
  font-style: italic;
`;

const NotesCard = styled(Card)`
  grid-column: 1 / -1;
`;

const NotesText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: ${({ theme }) => theme.typography.lineHeight.base};
  margin: 0;
  white-space: pre-wrap;
`;

const CardTitleWithIcon = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};

  svg {
    width: 16px;
    height: 16px;
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

// ==================== Helpers ====================

const formatDate = (iso?: string) => {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const calculateAge = (dob?: string): number | null => {
  if (!dob) return null;
  const birth = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
    age -= 1;
  }
  return age;
};

// ==================== Component ====================

export const OverviewTab = ({ client, onEditClick, canManage = false }: OverviewTabProps) => {
  const age = calculateAge(client.dateOfBirth) ?? client.age;

  return (
    <Grid>
      <Card>
        <CardHeader>
          <CardTitle>Demographics</CardTitle>
          {canManage && (
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Pencil size={14} />}
              onClick={onEditClick}
            >
              Edit
            </Button>
          )}
        </CardHeader>
        <InfoList>
          <InfoRow>
            <InfoIcon><User /></InfoIcon>
            <InfoContent>
              <InfoLabel>Full Name</InfoLabel>
              <InfoValue>{client.firstName} {client.lastName}</InfoValue>
            </InfoContent>
          </InfoRow>

          <InfoRow>
            <InfoIcon><Calendar /></InfoIcon>
            <InfoContent>
              <InfoLabel>Date of Birth</InfoLabel>
              {client.dateOfBirth ? (
                <InfoValue>
                  {formatDate(client.dateOfBirth)}
                  {age != null ? ` (Age ${age})` : ''}
                </InfoValue>
              ) : (
                <EmptyValue>Not provided</EmptyValue>
              )}
            </InfoContent>
          </InfoRow>

          <InfoRow>
            <InfoIcon><User /></InfoIcon>
            <InfoContent>
              <InfoLabel>Gender</InfoLabel>
              {client.gender ? (
                <InfoValue>{client.gender}</InfoValue>
              ) : (
                <EmptyValue>Not provided</EmptyValue>
              )}
            </InfoContent>
          </InfoRow>

          <InfoRow>
            <InfoIcon><Phone /></InfoIcon>
            <InfoContent>
              <InfoLabel>Phone</InfoLabel>
              {client.phone ? (
                <InfoValue>{client.phone}</InfoValue>
              ) : (
                <EmptyValue>Not provided</EmptyValue>
              )}
            </InfoContent>
          </InfoRow>

          <InfoRow>
            <InfoIcon><Mail /></InfoIcon>
            <InfoContent>
              <InfoLabel>Email</InfoLabel>
              {client.email ? (
                <InfoValue>{client.email}</InfoValue>
              ) : (
                <EmptyValue>Not provided</EmptyValue>
              )}
            </InfoContent>
          </InfoRow>

          <InfoRow>
            <InfoIcon><Hash /></InfoIcon>
            <InfoContent>
              <InfoLabel>External ID / MRN</InfoLabel>
              {client.externalId ? (
                <InfoValue>{client.externalId}</InfoValue>
              ) : (
                <EmptyValue>Not provided</EmptyValue>
              )}
            </InfoContent>
          </InfoRow>
        </InfoList>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Assignment</CardTitle>
        </CardHeader>
        <InfoList>
          <InfoRow>
            <InfoIcon><MapPin /></InfoIcon>
            <InfoContent>
              <InfoLabel>Primary Center</InfoLabel>
              {client.primaryCenter ? (
                <InfoValue>{client.primaryCenter.name}</InfoValue>
              ) : (
                <EmptyValue>Not assigned</EmptyValue>
              )}
            </InfoContent>
          </InfoRow>

          <InfoRow>
            <InfoIcon><User /></InfoIcon>
            <InfoContent>
              <InfoLabel>Assigned BCBA</InfoLabel>
              {/* `supervisingClinician`, NOT the legacy `assignee` relation. Both
                  create() and update() resolve the picker's member id into
                  `supervisingClinicianId` and never write the `assigneeId` column
                  (clients.service.ts), and SetOverseeingBcbaModal writes only
                  `supervisingClinicianId` too — so `client.assignee` is null for
                  every client set through the current UI, and this card read
                  "Not assigned" even when a BCBA was clearly assigned. */}
              {client.supervisingClinician ? (
                <InfoValue>
                  {client.supervisingClinician.firstName} {client.supervisingClinician.lastName}
                </InfoValue>
              ) : (
                <EmptyValue>Not assigned</EmptyValue>
              )}
            </InfoContent>
          </InfoRow>

          <InfoRow>
            <InfoIcon><Calendar /></InfoIcon>
            <InfoContent>
              <InfoLabel>Record Created</InfoLabel>
              <InfoValue>
                {formatDate(client.createdAt)}
                {/* Attribution is optional until the API lands createdBy (ADO #538). */}
                {client.createdBy && ` · ${fullName(client.createdBy)}`}
              </InfoValue>
            </InfoContent>
          </InfoRow>

          <InfoRow>
            <InfoIcon><Calendar /></InfoIcon>
            <InfoContent>
              <InfoLabel>Last Updated</InfoLabel>
              <InfoValue>
                {formatDate(client.updatedAt)}
                {client.updatedBy && ` · ${fullName(client.updatedBy)}`}
              </InfoValue>
            </InfoContent>
          </InfoRow>
        </InfoList>
      </Card>

      {client.notes && (
        <NotesCard>
          <CardHeader>
            <CardTitleWithIcon>
              <FileText />
              Notes
            </CardTitleWithIcon>
          </CardHeader>
          <NotesText>{client.notes}</NotesText>
        </NotesCard>
      )}
    </Grid>
  );
};
